'use client';

import { useRef } from 'react';
import { motion, useAnimationFrame, useMotionTemplate, useMotionValue, useTransform } from 'framer-motion';
import { cn } from '@/lib/cn';

interface MovingBorderProps extends React.ComponentPropsWithoutRef<'button'> {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
  borderRadius?: string;
  duration?: number;
  colors?: string[];
  as?: React.ElementType;
}

export function MovingBorder({
  children,
  className,
  containerClassName,
  borderRadius = '0.5rem',
  duration = 2000,
  colors = ['var(--gradient-primary-from)', 'var(--accent)', 'var(--gradient-primary-to)'],
  as: Tag = 'button',
  ...rest
}: MovingBorderProps) {
  const pathRef = useRef<SVGRectElement>(null);
  const progress = useMotionValue<number>(0);

  useAnimationFrame((time: number) => {
    const length = pathRef.current?.getTotalLength();
    if (length) {
      const pxPerMillisecond = length / duration;
      progress.set((time * pxPerMillisecond) % length);
    }
  });

  const x = useTransform(progress, (val: number) => {
    if (!pathRef.current) return 0;
    return pathRef.current.getPointAtLength(val).x;
  });
  const y = useTransform(progress, (val: number) => {
    if (!pathRef.current) return 0;
    return pathRef.current.getPointAtLength(val).y;
  });

  const transform = useMotionTemplate`translateX(${x}px) translateY(${y}px) translateX(-50%) translateY(-50%)`;

  const gradientColors = colors.join(', ');

  return (
    <Tag
      className={cn(
        'relative cursor-pointer overflow-hidden bg-transparent p-[1px] text-xl',
        containerClassName,
      )}
      style={{ borderRadius }}
      {...rest}
    >
      <div className="absolute inset-0" style={{ borderRadius }}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className="absolute h-full w-full"
          width="100%"
          height="100%"
        >
          <rect
            fill="none"
            width="100%"
            height="100%"
            rx={borderRadius}
            ry={borderRadius}
            ref={pathRef}
          />
        </svg>
        <motion.div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'inline-block',
            transform,
          }}
        >
          <div
            className="size-20 opacity-80 blur-[6px]"
            style={{
              background: `radial-gradient(circle, ${gradientColors})`,
              borderRadius: '50%',
            }}
          />
        </motion.div>
      </div>
      <div
        className={cn('relative z-10 flex items-center justify-center', className)}
        style={{ borderRadius: `calc(${borderRadius} - 1px)` }}
      >
        {children}
      </div>
    </Tag>
  );
}
